import ISmartExchangeClaims from '@/models/se/ISmartExchangeClaims';
import BaseApiService from '../BaseApiService';
import WebEnvConfig from '@/models/WebEnvConfig';
import SmartExchangeRequest from '@/models/requests/SmartExchangeRequest';
import IFeedData from '@/models/comp/IFeedData';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import SmartExchangeSubsRequest from '@/models/requests/SmartExchangeSubsRequest';
import IServiceHelper from './IServiceHelper';
import ServiceHelperFactory from './ServiceHelperFactory';
import _FakePayload from './_FakeCompPayload';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { isFakeDataHostnameAllowed } from '@/constants/FakeDataHostnames';

export default class SmartExchangeService extends BaseApiService {
    private readonly currentHostname: string;

    constructor(webEnvConfig: WebEnvConfig) {
        super(webEnvConfig.urls?.quickSourceApi ?? '');
        this.currentHostname = String(webEnvConfig.hostname || '').toLowerCase();
    }

    getServiceHelper(): IServiceHelper {
        return ServiceHelperFactory.getHelper();
    }

    async getBestComp(request: SmartExchangeRequest): Promise<any | null> {
        this.addContentTypeJsonHeader();
        this.addAcceptJsonHeader();
        //this.setLogRequest(true);

        try {
            const response = await this.axiosInstance.post('v7/DataDiscrepancy/SmartExchangeBestComp', request);
            
            // This is a check to see if we should load fake data for testing purposes.  
            // Only localhost and dev environments are allowed to load fake data.
            if (
                isFakeDataHostnameAllowed(this.currentHostname) &&
                theLocalStorageUtils.hasKey('load-fake-data') &&
                theLocalStorageUtils.get('load-fake-data')
            ) {
                if(request.isSubject) {
                    return _FakePayload.loadFakeData(SourceDataInitials.SmartExchange, true);
                } else {
                    return _FakePayload.loadFakeData(SourceDataInitials.SmartExchange, false);
                }
            } else {
                if (response.data) {
                    if (response.status === 200) {
                        return response.data;
                    }
                } else {
                    throw new Error(`SmartExchangeService.getBestComp(), No data returned from endpoint for address[${CompAddressUtils.getDescription(request.address)}], isSubject[${request.isSubject}], formCode[${request.formCode}]`);
                }
            }
            
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            const addressDescription = CompAddressUtils.getDescription(request.address);
            //console.log(`SmartExchangeService.getBestComp(), Error[${errorDescription}] on address[${addressDescription}], isSubject[${request.isSubject}], formCode[${request.formCode}]`);
        }

        return null;
    }

    async getSubComps(request: SmartExchangeSubsRequest): Promise<any[] | null> {
        this.addContentTypeJsonHeader();
        this.addAcceptJsonHeader();
        //this.setLogRequest(true);

        try {
            const data = JSON.stringify(request);

            const response = await this.axiosInstance.post('v7/DataDiscrepancy/SmartExchangeComps', data);
            if (response.status === 200) {
                const result: any[] = response.data;
                return result;
            }
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            const addressDescription = CompAddressUtils.getDescription(request.address);
            console.log(`SmartExchangeService.getSubComps(), Error[${errorDescription}] on address[${addressDescription}], formCode[${request.formCode}]`);
        }

        return null;
    }

    async getClaims(userLoginToken: string): Promise<ISmartExchangeClaims | null> {
        this.addContentTypeJsonHeader();
        this.addAcceptJsonHeader();
        //this.setLogRequest(true);

        try {
            const data = JSON.stringify(userLoginToken);
            const response = await this.axiosInstance.post('v1.0/DataDiscrepancy/SmartExchangeClaims', data);
            const result: ISmartExchangeClaims = {
                userAccessToken: response.data.userAccessToken,
                isOptedIn: response.data.isOptedIn,
                optInUrl: response.data.optInUrl,
                statusCode: response.status,
            };

            return result;
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            console.log(`SmartExchangeService.getClaims(), Error[${errorDescription}]`);
        }

        return null;
    }
}
